import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import { renderHourTick } from "../../../components/recharts/components";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { SearchBox } from "../../../components/Search";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import Tooltip from "../../../components/tooltip";
import {
  getGridcoMeter,
  getSubStationMetrics,
} from "../../../Rest_api/RestApi";
import { formatDateForAPI, R_CHART_TYPES } from "../const";
import { GridcoMeterData } from "../../../models/gridco";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { getMonthFromDate } from "../../../extensions/date";
import { DEMAND_UNITS } from "../Genration/Data";
import { BrushStart } from "../../../models/chart_model";
import { useSelector } from "react-redux";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { stringToConsistentColor } from "../../../extensions/string";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import MetricChip from "../../../components/Metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { GridcoMeterstationData } from "../../../models/metrics";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";
const BLOCKS_96 = Array.from({ length: 96 }, (_, i) => i + 1);

let Intervals = [
  {
    label: "Time Slot",
    key: "time",
    values: ["slot", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const GROUP_BY = [
  {
    label: "Discom",
    key: "discom",
    value: "discom",
  },
  {
    label: "Station",
    key: "station",
    value: "station",
  },
  // {
  //   label: "Meter Number",
  //   key: "meter_number",
  //   value: "meter_number",
  // },
];

const discomOptions = ["TPCODL", "TPSODL", "TPWODL", "TPNODL"];

const GridcoMeter = () => {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const [selectedUnit, setSelectedUnit] = useState(DEMAND_UNITS[0]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [data, setData] = useState<GridcoMeterData[]>([]);
  const [selectedDiscom, setSelectedDiscom] = useState<any[]>(["TPCODL"]);
  const [discomData, setDiscomData] = useState<any[]>(discomOptions);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [uniqueStations, setUniqueStations] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState([GROUP_BY[0]]);
  const [selectedStation, setSelectedStation] = useState<any[]>([]);
  const [meterNumber, setMeterNumber] = useState<any[]>([]);
  const [selectedMetereNumber, setSelectedMeterNumber] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<GridcoMeterstationData | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const fetchMetrics = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultRangeDate) {
      setMetrics(null);
      return;
    }

    setMetricsLoading(true);
    try {
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      const metricsData = await getSubStationMetrics({
        ranges: [
          {
            startDate: startDateStr,
            endDate: endDateStr,
            blocks: selectedBlock.map((block) => block.value),
          },
        ],
        aggregated: true,
        discom: selectedDiscom.length > 0 ? selectedDiscom : undefined,
        station_name: selectedStation.map((item) => item.value),
        meter_number: selectedMetereNumber.map((item) => item.value),
      });

      setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [
    DefaultDates,
    activeTab,
    selectedBlock,
    selectedDiscom,
    selectedStation,
    selectedMetereNumber,
  ]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;

    const startDateStr = formatDateForAPI(dates.startDate);
    const endDateStr = formatDateForAPI(dates.endDate);
    const newData = await getGridcoMeter(
      startDateStr,
      endDateStr,
      selectedDiscom,
    );
    setData(newData);

    setUniqueDate(
      Array.from(
        new Set(
          newData
            .map((item: { date: any }) => item.date)
            .sort(
              (a: string | number | Date, b: string | number | Date) =>
                new Date(a).getTime() - new Date(b).getTime(),
            ),
        ),
      ),
    );
    setUniqueBlock(
      Array.from(new Set(newData.map((item: { slot: any }) => item.slot))),
    );

    // setDiscomData(
    //   Array.from(new Set(newData.map((item: { discom: any }) => item.discom)))
    // );
    setUniqueStations(
      Array.from(
        new Set(newData.map((item: { station: any }) => item.station)),
      ),
    );
    setLoading(false);
  }, [DefaultDates, activeTab, selectedDiscom]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  function handleUnitChange(params: string) {
    setSelectedUnit(params);
  }

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.key === "time") {
      return "slot";
    } else {
      return selectedInterval.key;
    }
  }, [selectedInterval.key]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  function summaryExtraKeys() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        if (selectedInterval.key === "month") {
          return ["month"];
        } else {
          return ["date", "slot"];
        }
      } else {
        return ["date", "slot"];
      }
    } else {
      return [selectedInterval.values[0]];
    }
  }

  function summaryExtraLabels() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        if (selectedInterval.key === "month") {
          return ["Month"];
        } else {
          return ["Date", "Slot"];
        }
      } else {
        return ["Date", "Slot"];
      }
    } else {
      return [selectedInterval.values[0]];
    }
  }
  const legends = useMemo(
    () =>
      chartKeys.map((key) => ({
        dataKey: `${key}_value_${selectedUnit.toLowerCase()}`,
        name: `${normalizeDisplayLabelWithoutUndefined(key)}`,
        type: (selectedChartType as any).value,
        stackId: (selectedChartType as any).stackId,
      })),
    [chartKeys, selectedChartType, selectedUnit],
  );

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels(),
    filename: "Sub Station with OA",
  };

  function secondDataKey() {
    if (selectedInterval.isTime) {
      return "date";
    } else {
      return undefined;
    }
  }

  useEffect(() => {
    let filteredData = data.map((item) => ({
      ...item,
      value_lu: (item.value_mwh || 0) / 100,
      month: getMonthFromDate(item.date),
    }));

    if (selectedDate.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDate.map((item) => item.value).includes(item.date),
      );
    }
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.slot),
      );
    }

    if (selectedDiscom.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDiscom.includes(item.discom),
      );
    }

    if (selectedStation.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedStation.map((item) => item.value).includes(item.station),
      );
    }

    setMeterNumber(
      Array.from(
        new Set(
          filteredData.map((item: { meter_number: any }) => item.meter_number),
        ),
      ),
    );

    if (selectedMetereNumber.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedMetereNumber
          .map((item) => item.value)
          .includes(item.meter_number),
      );
    }

    const par: ChartDataParams = {
      keys: selectedInterval.values,
      tableKeys: ["date", "slot"],
      groupBy: selectedGroupKeys.map((k) => k.key),
      volumeKey: "",
      aggregationKeys: ["value_mwh", "value_mw", "value_lu"],
      averageDuplicationKeys: ["slot", "date"],
      averageKeys: ["value_mw"],

      priceKey: "",
      data: filteredData,
    };
    const cData = FormatChartData(par);
    setTableData(cData.tableData || []);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [
    data,
    selectedInterval,
    selectedDate,
    selectedBlock,
    selectedUnit,
    selectedGroupKeys,
    selectedStation,
    selectedDiscom,
    selectedMetereNumber,
  ]);

  function _handleGroupByChange(index: number) {
    setSelectedGroupKeys([GROUP_BY[index]]);
  }

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Intervals"
            items={Intervals.map((item) => item.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(item, index) => {
              setSelectedInterval(Intervals[index]);
            }}
          />

          <FilterMenu
            title="Unit"
            items={DEMAND_UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              handleUnitChange(item);
            }}
          />

          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((item) => item.label)}
            selectedItems={selectedGroupKeys.map((item) => item.label)}
            onSelect={(item, index) => _handleGroupByChange(index)}
          />

          <FilterMenu
            title="Discom"
            items={discomData.map((item) => item)}
            selectedItems={selectedDiscom}
            onSelect={(item, index) => {}}
            setSelectedItems={setSelectedDiscom}
          />

          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(R_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
           <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChip
              icon={<PeakIcon width={20} height={19} />}
              items={[
                {
                  label: "Peak Load",
                  value: metrics.meter["Peak Load (MW)"].value,
                  unit: metrics.meter["Peak Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<AvgIcon width={20} height={19} />}
              items={[
                {
                  label: "Average Load",
                  value: metrics.meter["Average Load (MW)"].value,
                  unit: metrics.meter["Average Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<MinIcon width={20} height={19} />}
              items={[
                {
                  label: "Minimum Load",
                  value: metrics.meter["Minimum Load (MW)"].value,
                  unit: metrics.meter["Minimum Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<TotalIcon width={20} height={19} />}
              items={[
                {
                  label: "Total Energy",
                  value: metrics.meter["Total Energy (MU)"].value,
                  unit: metrics.meter["Total Energy (MU)"].unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Sub Station"
              subtitle="Fetching Gridco sub station with OA meter data…"
            />
          ) : (
            <>
              <div className="gas-heading ">
                <h2 className="chartHeading w-80">
                  Sub Station with OA ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 w-full justify-end">
                  <SearchBox
                    options={uniqueStations.map((item) => {
                      return { value: item, label: item };
                    })}
                    isMany={true}
                    placeholder="Search stations"
                    selectedOptions={selectedStation}
                    onChange={setSelectedStation}
                  />

                  <SearchBox
                    options={meterNumber.map((item) => {
                      return { value: item, label: item };
                    })}
                    isMany={true}
                    placeholder="Search Meter Number"
                    selectedOptions={selectedMetereNumber}
                    onChange={setSelectedMeterNumber}
                  />
                  <BlockRangeSelector
                    blocks={BLOCKS_96}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      dataTab.label === "Chart" &&
                        downloadChartTableCSV(tablePar);
                      //

                      dataTab.label === "Summary" &&
                        downloadSummaryCSV(tablePar);

                      dataTab.label === "Table" &&
                        downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  unit={selectedUnit.toLowerCase()}
                  showBrush={chartData.length > 96 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={selectedInterval.isTime}
                  barCategoryGap={10}
                  yAxisLabel={selectedUnit}
                  legends={legends}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey={xDataKey}
                  onlyTitle={true}
                  secondXDataKey={secondDataKey()}
                  xTick={
                    selectedInterval.isTime && selectedBlock.length === 0
                      ? (value: any, index: any) => {
                          return renderHourTick(value, 10);
                        }
                      : undefined
                  }
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<p>Gridco : BSP bills & ABT Meters</p>}
              text="
              1. Some meter numbers in Annexure 1 were missing in provided ABT Meter files.
              2. Meter drawal is inclusive of OA drawal.
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
};

export default GridcoMeter;
